import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import { ArticleSourceType, ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { Box, Button, Checkbox, CircularProgress, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';

type PostFormDialogActionsProps = {
  article: ArticleType | undefined;
  articleType: ArticleSourceType;
  canPublishPost: boolean;
  createPlaylist: boolean;
  forward: boolean;
  isCreateDraft: boolean;
  isEditing: boolean;
  isSavingDraft: boolean;
  loadingFiles: boolean;
  startAnotherPost: boolean;
  onForward: () => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
  setStartAnotherPost: React.Dispatch<React.SetStateAction<boolean>>;
};

function PostFormDialogActions({
  article,
  articleType,
  canPublishPost,
  createPlaylist,
  forward,
  isCreateDraft,
  isSavingDraft,
  isEditing,
  loadingFiles,
  startAnotherPost,
  onForward,
  onSaveDraft,
  onSubmit,
  setStartAnotherPost,
}: PostFormDialogActionsProps) {
  const {
    formState: { isSubmitting },
  } = useFormContext<PostFormData>();

  return (
    <>
      {forward ? (
        <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%">
          <Button disabled={isSubmitting || loadingFiles} onClick={onForward} variant="contained">
            {isSubmitting ? (
              <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
            ) : (
              'Forward'
            )}
          </Button>
        </Box>
      ) : (
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Box display="flex" alignItems="center" ml={-1}>
            {isEditing && article?.isDraft && (
              <Button disabled={isSubmitting || loadingFiles} onClick={onSaveDraft}>
                {isSubmitting && isSavingDraft ? (
                  <CircularProgress
                    sx={(theme) => ({ color: theme.palette.custom.black })}
                    size={20}
                  />
                ) : (
                  'Update Draft'
                )}
              </Button>
            )}
            {!isEditing && !isCreateDraft && (
              <Button disabled={isSubmitting || loadingFiles} onClick={onSaveDraft}>
                {isSubmitting && isSavingDraft ? (
                  <CircularProgress
                    sx={(theme) => ({ color: theme.palette.custom.black })}
                    size={20}
                  />
                ) : (
                  'Save as Draft'
                )}
              </Button>
            )}
          </Box>
          <Box display="flex" alignItems="center">
            {isCreateDraft ? (
              <Button
                disabled={isSubmitting || loadingFiles}
                onClick={onSaveDraft}
                variant="contained"
              >
                {isSubmitting ? (
                  <CircularProgress
                    sx={(theme) => ({ color: theme.palette.custom.black })}
                    size={20}
                  />
                ) : (
                  'Save Draft'
                )}
              </Button>
            ) : !isCreateDraft && isEditing ? (
              (!article?.isDraft || canPublishPost) && (
                <Button
                  disabled={isSubmitting || loadingFiles}
                  onClick={onSubmit}
                  variant="contained"
                >
                  {isSubmitting && !isSavingDraft ? (
                    <CircularProgress
                      sx={(theme) => ({ color: theme.palette.custom.black })}
                      size={20}
                    />
                  ) : article?.isDraft ? (
                    articleType === ArticleSourceType.QUESTION ||
                    articleType === ArticleSourceType.SUGGESTION ? (
                      'Submit'
                    ) : article.originIndicator == 'PLAYLIST' ? (
                      'Create Collection'
                    ) : (
                      'Post'
                    )
                  ) : (
                    'Save'
                  )}
                </Button>
              )
            ) : (
              <>
                {!createPlaylist &&
                  articleType !== ArticleSourceType.QUESTION &&
                  articleType !== ArticleSourceType.SUGGESTION && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mr: 2,
                      }}
                    >
                      <Checkbox
                        onChange={(event) => {
                          setStartAnotherPost(event.target.checked);
                        }}
                        checked={startAnotherPost}
                      />
                      <Typography>Start another post</Typography>
                    </Box>
                  )}

                {canPublishPost && (
                  <Button
                    disabled={isSubmitting || loadingFiles}
                    onClick={onSubmit}
                    variant="contained"
                  >
                    {isSubmitting && !isSavingDraft ? (
                      <CircularProgress
                        sx={(theme) => ({ color: theme.palette.custom.black })}
                        size={20}
                      />
                    ) : createPlaylist ? (
                      'Create Collection'
                    ) : articleType === ArticleSourceType.QUESTION ||
                      articleType === ArticleSourceType.SUGGESTION ? (
                      'Submit'
                    ) : (
                      'Post'
                    )}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}

export default PostFormDialogActions;
